import { useNavigateWithReturnState } from 'utils/useNavigateWithReturnState';
import ListCreateButton from 'components/buttons/ListCreateButton';
import { ApartmentOutlined as ApartmentOutlinedIcon } from 'components/icons';
import { useMemo } from 'react';

import { Link as RouterLink } from 'react-router-dom';

import type { DataTableColumnDef } from 'components/tables/data-table';
import { FilterConfig } from 'components/tables/list-table/ListTableTypes';
import ListTableWithFilters from 'components/tables/list-table/ListTableWithFilters';
import { DraftNameCell } from 'components/drafts/DraftNameCell';
import { useWithDrafts } from 'components/drafts/useWithDrafts';
import { useLazyGetOfficesWithFiltersQuery } from 'store/api/offices';
import { Office } from 'types/offices';
import { getAddress } from 'utils/formUtil';
import { t } from 'utils/getTranslations';
import { officeInactiveSuffix } from 'utils/officeOptionRenderer';

const getOfficeHref = (office: Office) => `/office/${office.id}`;

const OfficesTable = () => {
    const navigateWithReturn = useNavigateWithReturnState();
    const [fetchOffices, { data, isFetching }] = useLazyGetOfficesWithFiltersQuery();
    const fetchData = useWithDrafts(fetchOffices);

    const columns = useMemo<DataTableColumnDef<Office>[]>(
        () => [
            {
                header: t('name'),
                accessorKey: 'name',
                enableSorting: false,
                meta: {
                    widthWeight: 1,
                    minPx: 120,
                },
                cell: ({ cell }) => {
                    const { id, name, isActive, isDraft } = cell.row.original;
                    return (
                        <DraftNameCell draft={isDraft ? { type: 'office', id } : null}>
                            <RouterLink
                                className="entity-link font-semibold whitespace-nowrap hover:underline"
                                to={`/office/${id}`}
                            >
                                {name || (isDraft ? t('new-entity.office') : '-')}
                                {officeInactiveSuffix({ isActive })}
                            </RouterLink>
                        </DraftNameCell>
                    );
                },
            },
            {
                header: t('initials'),
                accessorKey: 'abbreviation',
                enableSorting: false,
                meta: {
                    widthWeight: 1,
                    minPx: 90,
                },
                cell: ({ getValue }) => getValue<Office['abbreviation']>() ?? '',
            },
            {
                header: t('offices.companies'),
                accessorKey: 'companies',
                enableSorting: false,
                meta: {
                    widthWeight: 1,
                    minPx: 120,
                },
                cell: ({ getValue }) => {
                    const value = getValue<Office['companies']>();
                    return value ? value.map((company) => company.name).join(',') : '';
                },
            },
            {
                header: t('email'),
                accessorKey: 'emailAddress',
                enableSorting: false,
                meta: {
                    widthWeight: 1.4,
                    minPx: 180,
                },
                cell: ({ getValue }) => getValue<Office['emailAddress']>() ?? '',
            },
            {
                header: t('phone'),
                accessorKey: 'phoneNumber',
                enableSorting: false,
                meta: {
                    widthWeight: 1,
                    minPx: 110,
                },
                cell: ({ getValue }) => getValue<Office['phoneNumber']>() ?? '',
            },
            {
                header: t('address'),
                accessorKey: 'address',
                enableSorting: false,
                meta: {
                    widthWeight: 1,
                    minPx: 120,
                },
                cell: ({ getValue }) => {
                    const value = getValue<Office['address']>();
                    return value ? getAddress(value, value.countryCode) : '';
                },
            },
        ],
        []
    );

    const filterConfig: FilterConfig = useMemo(
        () => ({
            quickFilters: [],
            filters: [],
            searchBarSelectors: [
                { selector: 'name' },
                { selector: 'abbreviation' },
                { selector: 'company.name' },
                { selector: 'phoneNumber', comparator: '=phonelk=' },
                { selector: 'emailAddress' },
                { selector: 'address.address1' },
                { selector: 'address.address2' },
                { selector: 'address.cityName' },
                { selector: 'address.countryCode' },
                { selector: 'address.zipCode' },
            ],
        }),
        []
    );

    return (
        <ListTableWithFilters<Office>
            getRowHref={getOfficeHref}
            // Managed v8 engine for the weighted-width layout; column picker / reorder / resize
            // stay OFF (flags default false) — this table never had them.
            columnConfigId="offices"
            columns={columns}
            data={data}
            isLoading={isFetching}
            pluralTextId="offices.offices"
            singularTextId="office"
            noEntriesId="offices.no-offices-found"
            noEntriesBodyId="offices.empty.body"
            noEntriesIcon={ApartmentOutlinedIcon}
            noEntriesAction={
                <ListCreateButton
                    permissions={['CREATE.OFFICES']}
                    variant="contained"
                    color="secondary"
                    onClick={() => navigateWithReturn('/office/new')}
                    labelId="offices.add-new-office"
                    newLabelId="new-entity.office"
                />
            }
            fetchData={fetchData}
            filterConfig={filterConfig}
            rowsPerPage={50}
        />
    );
};

export default OfficesTable;
